/**
 * AWS Shadow AI Discovery Service
 *
 * API client for the Shadow AI Discovery feature. Talks to the
 * `/api/v1/aws-discovery` router. Mirrors the style of `billingService.ts`
 * and `llmScanService.ts` — thin wrappers over the shared `apiClient`, which
 * handles auth and error mapping. Field names mirror the backend schemas in
 * `src/app/schemas/aws_discovery.py` (the source of truth).
 */

import { apiClient } from "./api";
import type {
  AssetFilters,
  AwsDiscoveryConnection,
  AwsDiscoveryConnectionCreate,
  AwsDiscoverySetupInstructions,
  DiscoveredAssetDetail,
  DiscoveryScan,
  PaginatedAssets,
  PaginatedConnections,
} from "@/types/awsDiscovery";

const BASE_PATH = "/api/v1/aws-discovery";

export const awsDiscoveryService = {
  /**
   * Fetch a fresh ExternalId, CloudFormation template, and console steps for
   * the connect wizard. The ExternalId is server-issued and bound to the org.
   */
  async getSetupInstructions(): Promise<AwsDiscoverySetupInstructions> {
    return apiClient.get<AwsDiscoverySetupInstructions>(`${BASE_PATH}/setup-instructions`);
  },

  /**
   * Register a read-only cross-account role. The backend performs the
   * AssumeRole handshake before persisting and surfaces a mapped AWS error
   * message on failure — show that message verbatim. Returns the created
   * connection (201).
   */
  async createConnection(
    data: AwsDiscoveryConnectionCreate
  ): Promise<AwsDiscoveryConnection> {
    return apiClient.post<AwsDiscoveryConnection>(`${BASE_PATH}/connections`, data);
  },

  /**
   * List the org's discovery connections. The endpoint is paginated; callers
   * usually only need the `data` array.
   */
  async listConnections(): Promise<PaginatedConnections> {
    return apiClient.get<PaginatedConnections>(`${BASE_PATH}/connections`);
  },

  /**
   * Soft-delete a connection.
   */
  async deleteConnection(connectionId: number): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/connections/${connectionId}`);
  },

  /**
   * Enqueue a discovery scan for a connection. Returns 409 if a scan is
   * already running for that connection. The 202 body is the scan record.
   */
  async triggerScan(connectionId: number): Promise<DiscoveryScan> {
    return apiClient.post<DiscoveryScan>(
      `${BASE_PATH}/connections/${connectionId}/scan`
    );
  },

  /**
   * Poll a scan's status. Status moves through queued/running to a terminal
   * state (completed/partial/failed).
   */
  async getScan(scanId: number): Promise<DiscoveryScan> {
    return apiClient.get<DiscoveryScan>(`${BASE_PATH}/scans/${scanId}`);
  },

  /**
   * Fetch the paginated, filterable asset inventory.
   */
  async listAssets(filters?: AssetFilters): Promise<PaginatedAssets> {
    const params = new URLSearchParams();
    if (filters?.asset_type) params.append("asset_type", filters.asset_type);
    if (filters?.region) params.append("region", filters.region);
    if (filters?.connection_id !== undefined) {
      params.append("connection_id", String(filters.connection_id));
    }
    if (filters?.has_posture_flags !== undefined) {
      params.append("has_posture_flags", String(filters.has_posture_flags));
    }
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.items_per_page) {
      params.append("items_per_page", String(filters.items_per_page));
    }

    const queryString = params.toString();
    const url = `${BASE_PATH}/assets${queryString ? `?${queryString}` : ""}`;
    return apiClient.get<PaginatedAssets>(url);
  },

  /**
   * Fetch a single asset's detail, including the `scan_capability` gate that
   * decides whether the "run security scan" CTA is eligible.
   */
  async getAsset(assetId: number): Promise<DiscoveredAssetDetail> {
    return apiClient.get<DiscoveredAssetDetail>(`${BASE_PATH}/assets/${assetId}`);
  },
};
